import { tokens } from './tokens';

/**
 * Everything after `sl` is subscription-only: it comes from the CustomTransactions table
 * (a CPC balance-distribution sync, manual balance additions/removals, a balance corrected
 * back to what the exchange holds, and credited earn yield). An SPC result only ever
 * produces the first four.
 */
export type TxType =
  | 'buy'
  | 'sell'
  | 'tp'
  | 'sl'
  | 'dist'
  | 'deposit'
  | 'withdrawal'
  | 'correction'
  | 'interest';

/** Anything that is not a trade moves value IN or OUT rather than trading it. */
export type TxDirection = 'BUY' | 'SELL' | 'IN' | 'OUT';

/**
 * Fields shown in the transaction summary panel. The closed row, the expanded
 * row and the MRC deep-dive row all render these identically (via SummaryPanel)
 * so the values never shift or drift between views.
 */
export interface SummaryTx {
  /** Unique within the list. Custom transactions live in their own table with
   * their own auto-increment, so their ids collide with core transaction ids —
   * they are carried NEGATED to keep every id-keyed record distinct. Render
   * them through `txDisplayId`. */
  id: number;
  type: TxType;
  orderType: TxDirection;
  date: string;
  price: string;
  profit: string;
  /** Fraction (0..1) of the balance held in stable at this transaction. */
  stableShare: number;
}

export interface TxItem extends SummaryTx {
  timestamp: number;
  stableQty: string;
  stableBal: string;
  assetQty: string;
  assetBal: string;
  fee: string;
  totalFees: string;
}

export interface DeepTx extends SummaryTx {
  stableQty: string;
  stableProfit: string;
  assetQty: string;
  assetProfit: string;
  stableBal: string;
  assetBal: string;
  fee: string;
  totalFees: string;
  /** Type-specific stat for QUANTITIES & BALANCES — take-profit size for a
   * take-profit, stop-loss threshold for a stop-loss, null for core. */
  extraStat: { label: string; value: string } | null;
}

export interface MrcPhase {
  name: 'INITIALISED' | 'PENDING' | 'RESET' | 'CANCELLED' | 'SWITCH';
  color: string;
  /** Timeframe this phase was evaluated on (e.g. '4h'). */
  timeFrame: string;
  startDate: string;
  endDate: string;
  startPrice: string;
  endPrice: string;
  startRsi: string;
  endRsi: string;
}

export const txColor = (type: TxType): string =>
  ({
    sell: tokens.red,
    buy: tokens.green,
    tp: tokens.yellow,
    sl: tokens.blue,
    dist: tokens.amber,
    deposit: tokens.cyan,
    withdrawal: tokens.cyan,
    correction: tokens.cyan,
    interest: tokens.cyan,
  }[type] || tokens.textMuted);

export const txLabel = (type: TxType): string =>
  ({
    sell: 'Core transaction',
    buy: 'Core transaction',
    tp: 'Take Profit',
    sl: 'Stop Loss',
    dist: 'Distribution',
    deposit: 'Balance added',
    withdrawal: 'Balance removed',
    // A correction writes a balance down to what the exchange actually holds. It is not a
    // withdrawal — no money left — and calling it one is how it came to read "Balance added".
    correction: 'Balance corrected',
    interest: 'Earn interest',
  }[type] || type);

export const txDirection = (tx: { orderType: TxDirection }): TxDirection =>
  tx.orderType;

/** Colour for the direction label — green for value coming in (BUY/IN), red
 * for value going out (SELL/OUT). */
export const txDirectionColor = (tx: { orderType: TxDirection }): string =>
  tx.orderType === 'BUY' || tx.orderType === 'IN' ? tokens.green : tokens.red;

export const txIsCore = (type: TxType): boolean =>
  type === 'sell' || type === 'buy';

/** A row that came from the CustomTransactions table rather than an order. */
export const txIsCustom = (type: TxType): boolean =>
  type === 'dist' ||
  type === 'deposit' ||
  type === 'withdrawal' ||
  type === 'correction' ||
  type === 'interest';

/** Custom-transaction ids are carried negated to stay unique against core
 * transaction ids; the list shows the real, positive id. */
export const txDisplayId = (id: number): number => Math.abs(id);

/** Label above a row's headline number. Custom transactions realise no profit —
 * they show the value that moved instead. */
export const txProfitLabel = (tx: SummaryTx): string => {
  if (txIsCustom(tx.type)) return 'VALUE MOVED';
  return tx.orderType === 'BUY' ? 'ASSET PROFIT' : 'STABLE PROFIT';
};
